// src/services/appService.js
import api from "../api";

const BASE = process.env.REACT_APP_REACT_APP_CD_API_ENDPOINT;

/** 1) 儲值：POST /app/auth/charge */
export async function chargeCoin({ points, storeId }) {
  try {
    const res = await api.post(`${BASE}/app/auth/charge`, {
      coin: points,
      storeId,
    });

    return res.data;
  } catch (err) {
    // 讓呼叫端可辨識 401
    if (err?.response?.status === 401) {
      const e = new Error("Unauthorized");
      e.code = "UNAUTHORIZED";
      throw e;
    }
    throw err;
  }
}

/** 2) 取得店家清單：GET /app/store */
export async function fetchStores() {
  const res = await api.get(`${BASE}/app/store`);

  return res.data; // 預期為陣列，每筆有 simulators[].bannerImages[]
}

/** 2a) 建立「包廂 banner 圖」的授權 URL（需 Bearer） */
export function buildBannerImageRequest(itemId, { baseUrl, token }) {
  // 給 <img src> 時，通常後端要 Cookie/Bearer。若無法帶 Header，就改用 <a download> 或改走代理。
  return {
    uri: `${baseUrl}/app/auth/banner/${itemId}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

/** 2b) 取得每個模擬器的第一張 banner 圖 URL（回傳 {simulatorId, imageId, request} 列表） */
export function getSimulatorFirstBannerRequests(store, { baseUrl, token }) {
  const sims = Array.isArray(store?.simulators) ? store.simulators : [];
  return sims
    .map((s) => {
      const first = Array.isArray(s?.bannerImages) ? s.bannerImages[0] : undefined;
      if (!first) return null;
      return {
        simulatorId: s.id,
        imageId: first,
        request: buildBannerImageRequest(first, { baseUrl, token }),
      };
    })
    .filter(Boolean);
}

/** 3) 取得會員資料（含店家對應 coin） */
export async function fetchMePopulated() {
  const url =
    `${BASE}/app/auth/me` +
    `?populate=storeAppUsers` +
    `&populate=storeAppUsers.store`
  const res = await api.get(url);

  return res.data;
}

/** 3a) 從 me 結果中取出指定店家的 coin */
export function pickCoinForStore(meData, storeId) {
  const list = Array.isArray(meData?.storeAppUsers) ? meData.storeAppUsers : [];
  const hit = list.find((x) => String(x?.store?.id) === String(storeId));
  return hit?.coin ?? 0;
}

/**
 * 3b) 方案試算：POST /app/appointment/simulator/preview-price
 * 用於取得實際付款金額與各種優惠折扣明細
 */
export async function previewPrice({
  startTime,
  endTime,
  planId,
  storeSimulatorId,
  storeDiscountCode,    // 可選：優惠碼
  appUserCouponId,      // 可選：優惠券 ID
  status = "pending",
}) {
  const payload = {
    startTime,
    endTime,
    planId,
    status,
    storeSimulatorId,
    ...(storeDiscountCode ? { storeDiscountCode } : {}),
    ...(appUserCouponId ? { appUserCouponId } : {}),
  };

  try {
    const res = await api.post(`${BASE}/app/appointment/simulator/preview-price`, payload);
    // response: { amount, originAmount, totalCouponDiscountPrice, totalSpecialDiscountPrice, userTypeDiscountPrice, storeDiscountCodeDiscountPrice }
    console.log(res.data);
    return res.data;
  } catch (err) {
    if (err?.response?.status === 401) {
      const e = new Error("Unauthorized");
      e.code = "UNAUTHORIZED";
      throw e;
    }
    throw err;
  }
}

/**
 * 4) 使用點數建立預約：
 * - 一般： POST /app/appointment/{storeStatus}/coin
 * - 超時續約：POST /app/appointment/simulator/over-time/{orderId}/coin
 *
 * 備註：401 會丟 {code:'UNAUTHORIZED'}
 */
export async function startAppointmentWithCoin({
  orderId,                // 可選，存在表示超時續約
  storeStatus,            // 'simulator' | 'ground'
  startTime,
  endTime,
  planId,
  venueId,                // 對應 storeSimulatorId 或 storeGroundId
  couponId,               // 可選 appUserCouponId
  status = "pending",
}) {
  const isOverTime = orderId !== undefined && orderId !== null;
  const path = isOverTime
    ? `${BASE}/app/appointment/simulator/over-time/${orderId}/coin`
    : `${BASE}/app/appointment/${storeStatus}/coin`;

  // Set seconds to 0 and add 8 hours
  const startDate = new Date(startTime);
  startDate.setSeconds(0, 0);
  startDate.setHours(startDate.getHours() + 8);
  const endDate = new Date(endTime);
  endDate.setSeconds(0, 0);
  endDate.setHours(endDate.getHours() + 8);

  const isSimulator = storeStatus === "simulator";
  const payload = {
    startTime: startDate.toISOString(),
    endTime: endDate.toISOString(),
    planId,
    status,
    ...(isSimulator ? { storeSimulatorId: venueId } : { storeGroundId: venueId }),
    ...(couponId ? { appUserCouponId: couponId } : {}),
  };

  try {
    console.log("[startAppointmentWithCoin] path:", path);
    console.log("[startAppointmentWithCoin] request:", payload);
    const res = await api.post(path, payload);
    console.log("[startAppointmentWithCoin] response:", res.data);
    return res.data;
  } catch (err) {
    console.error("[startAppointmentWithCoin] error:", err);
    if (err?.response?.status === 401) {
      const e = new Error("Unauthorized");
      e.code = "UNAUTHORIZED";
      throw e;
    }
    throw err;
  }
}

/** 5) 取得儲值優惠圖示 URL（店家 chargeImages 的第一張） */
export function buildChargeImageUrl(storeData, { baseUrl }) {
  const first = Array.isArray(storeData?.chargeImages) ? storeData.chargeImages[0] : undefined;
  if (!first) return null;
  return `${baseUrl}/store/charge-image/${first}`;
}
